/**
 * Orchestration Store - Zustand state management for real-time orchestration
 * Manages agent activities, workflows, and orchestration connection state
 * Implements Epic 3 intelligence-driven state management patterns
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  OrchestrationConnection,
  AgentActivity,
  WorkflowExecution,
  AgentCommand,
  CommandResult,
  WorkflowDefinition
} from '../services/orchestration/OrchestrationConnection';

export interface AttentionPriority {
  id: string;
  type: 'agent' | 'workflow' | 'alert';
  priority: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  timestamp: number;
  actionRequired?: boolean;
}

export interface FocusContext {
  type: 'agent' | 'workflow' | 'overview';
  targetId?: string;
  title: string;
  description?: string;
}

export interface Decision {
  id: string;
  title: string;
  description: string;
  options: Array<{
    id: string;
    label: string;
    description?: string;
  }>;
  requiredBy?: number;
  priority: 'low' | 'medium' | 'high';
}

export interface WorkspaceConfiguration {
  layout: 'default' | 'agent-focused' | 'workflow-focused';
  activeAgents: string[];
  visibleMetrics: string[];
  alertFilters: string[];
}

interface OrchestrationState {
  // Connection state
  isConnected: boolean;
  connectionStatus: string;
  lastConnectionAttempt?: number;
  
  // Agent state
  agents: Record<string, AgentActivity>;
  agentStatistics: {
    total: number;
    active: number;
    idle: number;
    error: number;
  };
  
  // Workflow state
  activeWorkflows: Record<string, WorkflowExecution>;
  workflowHistory: WorkflowExecution[];
  
  // UI state
  currentFocus: FocusContext;
  attentionPriorities: AttentionPriority[];
  pendingDecisions: Decision[];
  contextualWorkspace: WorkspaceConfiguration;
  
  // Performance metrics
  updateLatency: number;
  lastUpdateTime: number;
  performanceMetrics: {
    batchProcessingTime?: number;
    lastBatchSize?: number;
    totalBatchesProcessed?: number;
    averageLatency?: number;
    connectionUptime?: number;
  };
  
  // Actions
  initializeConnection: () => Promise<void>;
  disconnect: () => void;
  updateAgentActivity: (agentId: string, activity: Partial<AgentActivity>) => void;
  updateWorkflowProgress: (workflowId: string, workflow: Partial<WorkflowExecution>) => void;
  executeAgentCommand: (command: AgentCommand) => Promise<CommandResult>;
  startWorkflow: (workflow: WorkflowDefinition) => Promise<WorkflowExecution>;
  setFocus: (focus: FocusContext) => void;
  addAttentionPriority: (priority: AttentionPriority) => void;
  removeAttentionPriority: (id: string) => void;
  addDecision: (decision: Decision) => void;
  resolveDecision: (id: string, optionId: string) => void;
  updateWorkspaceConfiguration: (config: Partial<WorkspaceConfiguration>) => void;
  subscribeToAgent: (agentId: string) => void;
  subscribeToWorkflow: (workflowId: string) => void;
}

export const useOrchestrationStore = create<OrchestrationState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    isConnected: false,
    connectionStatus: 'disconnected',
    agents: {},
    agentStatistics: {
      total: 0,
      active: 0,
      idle: 0,
      error: 0
    },
    activeWorkflows: {},
    workflowHistory: [],
    currentFocus: {
      type: 'overview',
      title: 'Agent Orchestration Overview',
      description: 'Real-time view of all agent activities and workflows'
    },
    attentionPriorities: [],
    pendingDecisions: [],
    contextualWorkspace: {
      layout: 'default',
      activeAgents: [],
      visibleMetrics: ['performance', 'status', 'progress'],
      alertFilters: ['high', 'critical']
    },
    updateLatency: 0,
    lastUpdateTime: Date.now(),
    performanceMetrics: {
      batchProcessingTime: 0,
      lastBatchSize: 0,
      totalBatchesProcessed: 0,
      averageLatency: 0,
      connectionUptime: 0
    },

    // Actions
    initializeConnection: async () => {
      const startTime = Date.now();
      set({ connectionStatus: 'connecting', lastConnectionAttempt: startTime });

      try {
        await OrchestrationConnection.connect();
        
        // Enable high-frequency mode for real-time updates
        OrchestrationConnection.enableHighFrequencyMode?.();
        
        // Subscribe to batch updates for all tracked agents
        const currentState = get();
        const agentIds = Object.keys(currentState.agents);
        if (agentIds.length > 0) {
          OrchestrationConnection.subscribeToAgentBatch?.(agentIds);
        }
        
        // Set up event listeners
        OrchestrationConnection.on('connected', (connected: boolean) => {
          set({ 
            isConnected: connected,
            connectionStatus: connected ? 'connected' : 'disconnected',
            updateLatency: Date.now() - startTime,
            lastUpdateTime: Date.now()
          });
        });

        // Performance monitoring
        OrchestrationConnection.on('batchProcessed', (metrics: { processingTime: number; count: number }) => {
          const currentMetrics = get().performanceMetrics;
          set({
            performanceMetrics: {
              ...currentMetrics,
              batchProcessingTime: metrics.processingTime,
              lastBatchSize: metrics.count,
              totalBatchesProcessed: (currentMetrics.totalBatchesProcessed || 0) + 1
            }
          });
        });

        OrchestrationConnection.on('agentActivity', (activity: AgentActivity) => {
          get().updateAgentActivity(activity.agentId, activity);
        });

        OrchestrationConnection.on('workflowProgress', (workflow: WorkflowExecution) => {
          get().updateWorkflowProgress(workflow.workflowId, workflow);
        });

        OrchestrationConnection.on('agentStatusUpdate', (update: { agentId: string; status: string }) => {
          get().updateAgentActivity(update.agentId, { status: update.status });
        });

        OrchestrationConnection.on('systemAlert', (alert: { priority?: string; message: string; actionRequired?: boolean }) => {
          get().addAttentionPriority({
            id: `alert_${Date.now()}`,
            type: 'alert',
            priority: alert.priority || 'medium',
            message: alert.message,
            timestamp: Date.now(),
            actionRequired: alert.actionRequired
          });
        });

        // Handle performance alerts
        OrchestrationConnection.on('performanceAlert', (alert: { type: string; latency: number; threshold: number; timestamp: number }) => {
          const priority = alert.type === 'latency_critical' ? 'critical' : 'medium';
          get().addAttentionPriority({
            id: `perf_alert_${Date.now()}`,
            type: 'alert',
            priority,
            message: `Performance Alert: ${alert.latency}ms latency exceeds ${alert.threshold}ms threshold`,
            timestamp: alert.timestamp,
            actionRequired: alert.type === 'latency_critical'
          });
        });

        set({ 
          isConnected: true,
          connectionStatus: 'connected',
          updateLatency: Date.now() - startTime,
          lastUpdateTime: Date.now()
        });

      } catch (error) {
        console.error('Failed to initialize orchestration connection:', error);
        set({ 
          isConnected: false,
          connectionStatus: 'error',
          lastUpdateTime: Date.now()
        });
      }
    },

    disconnect: () => {
      OrchestrationConnection.disconnect();
      set({ 
        isConnected: false,
        connectionStatus: 'disconnected',
        lastUpdateTime: Date.now()
      });
    },

    updateAgentActivity: (agentId: string, activity: Partial<AgentActivity>) => {
      const startTime = Date.now();
      
      set((state) => {
        const updatedAgents = {
          ...state.agents,
          [agentId]: {
            ...state.agents[agentId],
            ...activity,
            lastUpdated: Date.now()
          }
        };

        // Recalculate statistics
        const statistics = Object.values(updatedAgents).reduce(
          (stats, agent) => {
            stats.total++;
            switch (agent.status) {
              case 'executing':
                stats.active++;
                break;
              case 'idle':
                stats.idle++;
                break;
              case 'error':
                stats.error++;
                break;
            }
            return stats;
          },
          { total: 0, active: 0, idle: 0, error: 0 }
        );

        return {
          agents: updatedAgents,
          agentStatistics: statistics,
          updateLatency: Date.now() - startTime,
          lastUpdateTime: Date.now()
        };
      });
    },

    updateWorkflowProgress: (workflowId: string, workflow: Partial<WorkflowExecution>) => {
      set((state) => ({
        activeWorkflows: {
          ...state.activeWorkflows,
          [workflowId]: {
            ...state.activeWorkflows[workflowId],
            ...workflow
          }
        },
        lastUpdateTime: Date.now()
      }));
    },

    executeAgentCommand: async (command: AgentCommand): Promise<CommandResult> => {
      return await OrchestrationConnection.executeAgentCommand(command);
    },

    startWorkflow: async (workflow: WorkflowDefinition): Promise<WorkflowExecution> => {
      const execution = await OrchestrationConnection.executeWorkflow(workflow);
      
      set((state) => ({
        activeWorkflows: {
          ...state.activeWorkflows,
          [execution.workflowId]: execution
        },
        lastUpdateTime: Date.now()
      }));

      return execution;
    },

    setFocus: (focus: FocusContext) => {
      set({ 
        currentFocus: focus,
        lastUpdateTime: Date.now()
      });
    },

    addAttentionPriority: (priority: AttentionPriority) => {
      set((state) => ({
        attentionPriorities: [priority, ...state.attentionPriorities]
          .sort((a, b) => {
            const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
            return priorityOrder[b.priority] - priorityOrder[a.priority];
          })
          .slice(0, 50), // Keep only latest 50 priorities
        lastUpdateTime: Date.now()
      }));
    },

    removeAttentionPriority: (id: string) => {
      set((state) => ({
        attentionPriorities: state.attentionPriorities.filter(p => p.id !== id),
        lastUpdateTime: Date.now()
      }));
    },

    addDecision: (decision: Decision) => {
      set((state) => ({
        pendingDecisions: [...state.pendingDecisions, decision],
        lastUpdateTime: Date.now()
      }));
    },

    resolveDecision: (id: string, _optionId: string) => {
      set((state) => ({
        pendingDecisions: state.pendingDecisions.filter(d => d.id !== id),
        lastUpdateTime: Date.now()
      }));
    },

    updateWorkspaceConfiguration: (config: Partial<WorkspaceConfiguration>) => {
      set((state) => ({
        contextualWorkspace: {
          ...state.contextualWorkspace,
          ...config
        },
        lastUpdateTime: Date.now()
      }));
    },

    subscribeToAgent: (agentId: string) => {
      OrchestrationConnection.subscribeToAgent(agentId);
    },

    subscribeToWorkflow: (workflowId: string) => {
      OrchestrationConnection.subscribeToWorkflow(workflowId);
    }
  }))
);

// Subscribe to store changes for performance monitoring
useOrchestrationStore.subscribe(
  (state) => state.lastUpdateTime,
  (updateTime) => {
    // Log performance metrics every 10 seconds
    if (updateTime && Date.now() - updateTime > 10000) {
      console.log('OrchestrationStore: Performance metrics', {
        updateLatency: useOrchestrationStore.getState().updateLatency,
        agentCount: useOrchestrationStore.getState().agentStatistics.total,
        workflowCount: Object.keys(useOrchestrationStore.getState().activeWorkflows).length,
        priorityCount: useOrchestrationStore.getState().attentionPriorities.length
      });
    }
  }
);
